class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.megaMenuContent = this.mainDetailsToggle.querySelector('.mega-menu__content');
    this.summary = this.mainDetailsToggle.querySelector('summary');

    if (this.megaMenuContent) {
      /* Warm every panel's images the moment the cursor reaches the menu
         button - before the click, and for ALL categories, not just the
         one shown first. */
      this.summary.addEventListener('pointerenter', () => this.preloadImages(), { once: true });

      /* A closed <details> hides its content the instant [open] drops, so
         a hide animation set on toggle can never be seen. Closing is a
         two-step: play menuHide, drop the attribute when it ends. The
         summary click must be intercepted for the same reason. */
      this.summary.addEventListener('click', (event) => {
        if (this.mainDetailsToggle.hasAttribute('open')) {
          event.preventDefault();
          this.animateClose();
        } else {
          /* Arm the item entrance BEFORE [open] lands. The toggle event
             is async, so arming there paints one frame of fully-visible
             items first, then restarts them from opacity 0 - a blink.
             Animations on unrendered content only start on first paint,
             so pre-arming is safe. */
          this.runItemEntrance();
        }
      });
      /* Capture-phase: beats global.js's onKeyUpEscape, which strips
         [open] directly and would skip the animation. */
      this.addEventListener(
        'keyup',
        (event) => {
          if (!event.code || event.code.toUpperCase() !== 'ESCAPE') return;
          if (!this.mainDetailsToggle.hasAttribute('open')) return;
          event.stopPropagation();
          this.animateClose();
          this.summary.focus();
        },
        true
      );
    }
  }

  close() {
    if (this.megaMenuContent && this.mainDetailsToggle.hasAttribute('open')) {
      this.animateClose();
    } else {
      super.close();
    }
  }

  animateClose() {
    if (this.closing) return;
    this.closing = true;
    this.megaMenuContent.style.animation = 'menuHide 150ms ease-in forwards';
    const finish = () => {
      if (!this.closing) return;
      this.closing = false;
      this.entranceArmed = false;
      this.megaMenuContent.removeEventListener('animationend', finish);
      super.close();
    };
    this.megaMenuContent.addEventListener('animationend', finish);
    /* animationend can be swallowed (reduced motion, tab hidden) - the
       menu must never get stuck open. */
    setTimeout(finish, 250);
  }

  preloadImages() {
    if (this.imagesPreloaded || !this.megaMenuContent) return;
    this.imagesPreloaded = true;
    /* Eager images fetch even inside hidden panels / a closed details. */
    this.megaMenuContent.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  }

  onToggle() {
    if (!this.header) return;

    const isOpen = this.mainDetailsToggle.hasAttribute('open');
    this.header.preventHide = isOpen;

    const headerBottomPosition = document.documentElement.style.getPropertyValue('--header-bottom-position-desktop');
    if (headerBottomPosition === '') {
      document.documentElement.style.setProperty(
        '--header-bottom-position-desktop',
        `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
      );
    }

    if (!this.megaMenuContent) return;

    if (isOpen) {
      this.closing = false;
      this.megaMenuContent.style.animation = 'menuShow 150ms ease-out forwards';
      document.body.style.overflow = 'hidden';
      this.preloadImages();

      /* Fallback for opens that skipped the summary click. */
      this.runItemEntrance();
    } else {
      if (!document.querySelector('.header__search details[open]')) {
        document.body.style.overflow = '';
      }
      this.megaMenuContent.style.animation = '';
      this.megaMenuContent.querySelectorAll('.mega-nav__rail-item, .mega-nav__tile').forEach((item) => {
        item.style.animation = '';
      });
      this.entranceArmed = false;
    }
  }

  /* Fast staggered entrance: rail rows, then the visible panel's tiles.
     Idempotent per open (armed once from the summary click, again as a
     no-op from onToggle). The animation must be REMOVED once it
     finishes: while an opacity animation is applied, every item is its
     own stacking context, which traps the rail rows' ::before shadow
     overlay beneath the neighboring row's art ('backwards', never
     'both' - a forwards fill would pin that state permanently). */
  runItemEntrance() {
    if (this.entranceArmed) return;
    this.entranceArmed = true;
    this.megaMenuContent
      .querySelectorAll('.mega-nav__rail-item, .mega-nav__panel:not([hidden]) .mega-nav__tile')
      .forEach((item, index) => {
        item.style.animation = `megaItemIn 180ms ease-out backwards ${index * 12}ms`;
        item.addEventListener(
          'animationend',
          () => {
            item.style.animation = '';
          },
          { once: true }
        );
      });
  }
}

customElements.define('header-menu', HeaderMenu);
